import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, RefreshCw, ShoppingBasket } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EXTERNAL_URLS } from '@/constants/config';
import type { components } from '@/types/api.generated';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ExternalServiceLink,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { CollapsibleCheck } from './CollapsibleCheck';
import { useBetterAuth } from '@hooks/useAuth';
import { http } from '@lib/api';
import { formatDateTime } from '@lib/datetime';
import { queryKeys } from '@lib/queryKeys';
import { soldOutProducts } from '../pending/soldOut';

type SalesBridge = components['schemas']['SalesBridge'];
type RefetchResult = components['schemas']['RefetchResult'];
type TruthReconcileReport = components['schemas']['DispatchTruthReconcileReport'];

// NOTICE is a routine fact worth a glance. ALARM (destructive) stays reserved
// for our records contradicting the upstream truth source.
const NOTICE_TEXT = 'font-medium text-foreground';

const FINANCIAL_STATUS_LABEL = {
  pending: 'dispatch.salesBridge.reason.pending',
  authorized: 'dispatch.salesBridge.reason.authorized',
  expired: 'dispatch.salesBridge.reason.expired',
  partially_paid: 'dispatch.salesBridge.reason.partiallyPaid',
  paid: 'dispatch.salesBridge.reason.paid',
  partially_refunded: 'dispatch.salesBridge.reason.partiallyRefunded',
  refunded: 'dispatch.salesBridge.reason.refunded',
  voided: 'dispatch.salesBridge.reason.voided',
} as const;

const PARSE_FAILURE_KIND_LABEL = {
  malformed_json: 'dispatch.salesBridge.parseFailure.kind.malformed_json',
  schema_invalid: 'dispatch.salesBridge.parseFailure.kind.schema_invalid',
  invalid_created_at: 'dispatch.salesBridge.parseFailure.kind.invalid_created_at',
  missing_order: 'dispatch.salesBridge.parseFailure.kind.missing_order',
  unknown: 'dispatch.salesBridge.parseFailure.kind.unknown',
} as const;

const PRODUCT_ANOMALY_LABEL = {
  menu_only: 'dispatch.salesBridge.anomaly.menuOnly',
  orders_only: 'dispatch.salesBridge.anomaly.ordersOnly',
  inactive_upcoming: 'dispatch.salesBridge.anomaly.inactiveUpcoming',
  active_stale: 'dispatch.salesBridge.anomaly.activeStale',
} as const;

type ProductAnomaly = keyof typeof PRODUCT_ANOMALY_LABEL;
type ProductAnomalyLabel = (typeof PRODUCT_ANOMALY_LABEL)[ProductAnomaly];

function financialStatusLabel(status: string | null): string {
  if (status !== null && Object.prototype.hasOwnProperty.call(FINANCIAL_STATUS_LABEL, status)) {
    return FINANCIAL_STATUS_LABEL[status as keyof typeof FINANCIAL_STATUS_LABEL];
  }
  return 'dispatch.salesBridge.reason.unknown';
}

function parseFailureKindLabel(kind: string): string {
  if (Object.prototype.hasOwnProperty.call(PARSE_FAILURE_KIND_LABEL, kind)) {
    return PARSE_FAILURE_KIND_LABEL[kind as keyof typeof PARSE_FAILURE_KIND_LABEL];
  }
  return PARSE_FAILURE_KIND_LABEL.unknown;
}

function productAnomalyLabel(identifier: string): ProductAnomalyLabel | null {
  if (Object.prototype.hasOwnProperty.call(PRODUCT_ANOMALY_LABEL, identifier)) {
    return PRODUCT_ANOMALY_LABEL[identifier as ProductAnomaly];
  }
  return null;
}

interface ChecklistSectionProps {
  /** Suffix of the row's `sales-bridge-check-<key>` test id. */
  checkKey: string;
  title: string;
  count: number;
  emptyText: string;
  error?: boolean;
  children?: ReactNode;
}

/**
 * One checklist row. A check with nothing to confirm collapses to its title +
 * "passed" badge (#1676): the operator still sees that it ran, without the
 * pile of green bodies that used to bury the one row needing action.
 */
function ChecklistSection({
  checkKey,
  title,
  count,
  emptyText,
  error = false,
  children,
}: ChecklistSectionProps) {
  const { t } = useTranslation();
  return (
    <CollapsibleCheck
      testId={`sales-bridge-check-${checkKey}`}
      title={title}
      defaultOpen={error || count > 0}
      badge={
        error ? (
          <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />
        ) : count === 0 ? (
          <span className="flex items-center gap-1 text-sm text-foreground">
            <Check className="h-4 w-4 text-success" aria-hidden="true" />
            {t('dispatch.salesBridge.passed')}
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-warning">
            <span aria-hidden="true">⚠️</span>
            <Badge variant="outline" className="border-warning/50 text-warning">
              {t('dispatch.salesBridge.needsConfirm', { count })}
            </Badge>
          </span>
        )
      }
    >
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {t('dispatch.salesBridge.error')}
        </p>
      ) : count === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        children
      )}
    </CollapsibleCheck>
  );
}

interface SalesBridgeRefetchButtonProps {
  date: string;
  orderName: string;
  shopifyId: string;
}

function SalesBridgeRefetchButton({ date, orderName, shopifyId }: SalesBridgeRefetchButtonProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const refetch = useMutation<RefetchResult, Error>({
    mutationFn: () => http.post<RefetchResult>(`/api/orders/${shopifyId}/refetch`),
    onSuccess: async (result) => {
      switch (result.kind) {
        case 'applied':
          toast.success(t('dispatch.salesBridge.refetchApplied'));
          break;
        case 'stale_event':
        case 'not_found_in_shopify':
          toast.info(t('dispatch.salesBridge.refetchNoChange'));
          break;
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.dispatch.salesBridge(date),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.dispatch.truthReconcile(date),
        }),
      ]);
    },
    onError: (error) => {
      toast.error(t('dispatch.salesBridge.refetchError'), error.message);
    },
  });

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="ml-auto gap-1.5"
      data-testid={`sales-bridge-refetch-${orderName}`}
      disabled={refetch.isPending}
      onClick={() => refetch.mutate()}
    >
      <RefreshCw
        className={`h-4 w-4 ${refetch.isPending ? 'animate-spin' : ''}`}
        aria-hidden="true"
      />
      {t(
        refetch.isPending
          ? 'dispatch.salesBridge.refetching'
          : 'dispatch.salesBridge.refetchShopify',
      )}
    </Button>
  );
}

interface SalesBridgeCardProps {
  date: string;
}

export function SalesBridgeCard({ date }: SalesBridgeCardProps) {
  const { t } = useTranslation();
  const { isAdmin } = useBetterAuth();
  const bridge = useQuery({
    queryKey: queryKeys.dispatch.salesBridge(date),
    queryFn: () =>
      http.get<SalesBridge>('/api/dispatch/sales-bridge', {
        params: { date },
      }),
    enabled: date.length > 0,
    staleTime: 60_000,
  });
  // PASSIVE read of the shared truth-reconcile cache — `enabled: false` means we
  // never fetch it (that costly Shopify call is owned by TruthReconcileCard). We
  // only annotate the unpaid list once the operator has run "Verify truth" and
  // populated this same cache slot.
  const truth = useQuery({
    queryKey: queryKeys.dispatch.truthReconcile(date),
    queryFn: () =>
      http.get<TruthReconcileReport>('/api/dispatch/truth-reconcile', {
        params: { date },
      }),
    enabled: false,
    staleTime: 30_000,
  });
  const data = bridge.data;
  // Orders D1 flags as unpaid but Shopify has actually settled: a per-order
  // financial DRIFT item (`order.financial.<name>` verdict `drift`). Matching on
  // the financial key precisely avoids cancel/orderset drift items.
  const financialKey = (name: string) => `order.financial.${name}`;
  const paidDespitePending = new Set(
    (truth.data?.items ?? [])
      .filter(
        (i) => i.verdict === 'drift' && i.orderName != null && i.key === financialKey(i.orderName),
      )
      .map((i) => i.orderName as string),
  );
  const paidCount = (data?.unpaid ?? []).filter((order) =>
    paidDespitePending.has(order.order_name),
  ).length;
  const soldOutCount = (data ? soldOutProducts(data) : []).length;
  const shopifyUnavailableReason = data?.shopify_menu?.unavailable_reason?.trim() || null;
  const showShopifyDisclosures = shopifyUnavailableReason === null;
  const empty =
    data !== undefined &&
    data.products.length === 0 &&
    data.exceptions.length === 0 &&
    data.missing_jobs.length === 0 &&
    data.unpaid.length === 0 &&
    data.zero_unit_orders.length === 0 &&
    data.multi_qty_detail.length === 0 &&
    (data.parse_failures?.length ?? 0) === 0;
  const allPassed =
    data !== undefined &&
    data.exceptions.length === 0 &&
    data.missing_jobs.length === 0 &&
    data.unpaid.length === 0 &&
    data.zero_unit_orders.length === 0 &&
    data.multi_qty_detail.length === 0 &&
    (data.parse_failures?.length ?? 0) === 0;

  return (
    <Card data-testid="dispatch-sales-bridge-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingBasket className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          {t('dispatch.salesBridge.title')}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t('dispatch.salesBridge.description', { date })}
        </p>
      </CardHeader>
      <CardContent>
        {bridge.isError ? (
          <p role="alert" className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            {t('dispatch.salesBridge.error')}
          </p>
        ) : bridge.isLoading ? (
          <p className="text-sm text-muted-foreground">{t('dispatch.salesBridge.loading')}</p>
        ) : empty ? (
          <p className="text-sm text-muted-foreground">{t('dispatch.salesBridge.empty')}</p>
        ) : data ? (
          <div className="space-y-4">
            {shopifyUnavailableReason ? (
              <section
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-3 text-destructive"
              >
                <p className="text-sm">
                  {t('dispatch.salesBridge.shopifyUnavailable', {
                    reason: shopifyUnavailableReason,
                  })}
                </p>
              </section>
            ) : data.shopify_menu?.as_of ? (
              <p className="text-sm text-muted-foreground">
                {t('dispatch.salesBridge.shopifyAsOf', {
                  at: formatDateTime(data.shopify_menu.as_of),
                })}
              </p>
            ) : null}

            <div className="space-y-3">
              <CollapsibleCheck
                testId="sales-bridge-check-products"
                title={t('dispatch.salesBridge.productsTitle')}
                defaultOpen
                badge={
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    {soldOutCount > 0 ? (
                      <span className={NOTICE_TEXT}>
                        {t('dispatch.salesBridge.productsSoldOut', { count: soldOutCount })}
                      </span>
                    ) : null}
                    {t('dispatch.salesBridge.productsCount', { count: data.products.length })}
                  </span>
                }
              >
                <ul className="divide-y rounded-md border">
                  {data.products.map((product) => {
                    const shopifyUrl = product.shopify_product_id
                      ? EXTERNAL_URLS.shopifyAdmin('products', product.shopify_product_id)
                      : null;
                    const showsInventory =
                      product.inventory?.tracked === true && product.inventory.quantity !== null;
                    const shopify = showShopifyDisclosures ? product.shopify : null;
                    const anomalyLabels = showShopifyDisclosures
                      ? Array.from(new Set(product.anomalies ?? []))
                          .map(productAnomalyLabel)
                          .filter((label): label is ProductAnomalyLabel => label !== null)
                      : [];
                    return (
                      <li
                        key={product.shopify_product_id ?? product.name}
                        className="flex flex-wrap items-start justify-between gap-2 px-3 py-2"
                      >
                        <div>
                          <p className="flex items-center gap-1.5 text-sm font-medium">
                            {product.name}
                            {shopifyUrl && (
                              <ExternalServiceLink
                                href={shopifyUrl}
                                ariaLabel={t('ui.openInShopify')}
                              />
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {t('dispatch.salesBridge.productCounts', {
                              units: product.units,
                              orders: product.orders,
                            })}
                            {product.multi_qty_orders > 0 ? (
                              <span className="ml-2 text-foreground">
                                {t('dispatch.salesBridge.multiQty', {
                                  count: product.multi_qty_orders,
                                })}
                              </span>
                            ) : null}
                          </p>
                          {anomalyLabels.length > 0 ? (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {anomalyLabels.map((label) => (
                                <Badge key={label} variant="outline">
                                  {t(label)}
                                </Badge>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-1">
                          {showsInventory ? (
                            <Badge variant="outline">
                              {t('dispatch.salesBridge.inventoryAvailable', {
                                count: product.inventory?.quantity,
                              })}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                          {shopify ? (
                            <Badge variant="outline">
                              {shopify.total_inventory === null
                                ? t('dispatch.salesBridge.shopifyInventoryUntracked')
                                : t('dispatch.salesBridge.shopifyInventoryAvailable', {
                                    count: shopify.total_inventory,
                                  })}
                            </Badge>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CollapsibleCheck>

              <ChecklistSection
                checkKey="missingJobs"
                title={t('dispatch.salesBridge.missingJobsTitle')}
                count={data.missing_jobs.length}
                emptyText={t('dispatch.salesBridge.missingJobsEmpty')}
              >
                <ul className="divide-y rounded-md border">
                  {data.missing_jobs.map((order) => (
                    <li key={order.order_name} className="px-3 py-2 text-sm font-medium">
                      {order.order_name}
                    </li>
                  ))}
                </ul>
              </ChecklistSection>

              <ChecklistSection
                checkKey="exceptions"
                title={t('dispatch.salesBridge.exceptionsTitle')}
                count={data.exceptions.length}
                emptyText={t('dispatch.salesBridge.exceptionsEmpty')}
              >
                <ul className="space-y-2">
                  {data.exceptions.map((exception) => (
                    <li
                      key={exception.order_name}
                      className="flex flex-wrap items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm"
                    >
                      <span className="font-medium">{exception.order_name}</span>
                      <Badge variant="outline">
                        {t(financialStatusLabel(exception.financial_status))}
                      </Badge>
                      <span className="text-muted-foreground">
                        {exception.units > 0
                          ? t('dispatch.salesBridge.exceptionUnits', {
                              count: exception.units,
                            })
                          : t('dispatch.salesBridge.removedRefundInfo')}
                      </span>
                      {isAdmin && exception.shopify_id ? (
                        <SalesBridgeRefetchButton
                          date={date}
                          orderName={exception.order_name}
                          shopifyId={exception.shopify_id}
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>
              </ChecklistSection>

              <ChecklistSection
                checkKey="zeroUnit"
                title={t('dispatch.salesBridge.zeroUnitTitle')}
                count={data.zero_unit_orders.length}
                emptyText={t('dispatch.salesBridge.zeroUnitEmpty')}
              >
                <div className="space-y-2">
                  <ul className="space-y-2">
                    {data.zero_unit_orders.map((order) => (
                      <li
                        key={order.order_name}
                        className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm"
                      >
                        <span className="font-medium">{order.order_name}</span>
                        <Badge variant="outline">
                          {t(financialStatusLabel(order.financial_status))}
                        </Badge>
                        <span className="text-muted-foreground">
                          {t('dispatch.salesBridge.zeroUnitJobStatuses', {
                            statuses: order.job_statuses
                              .map((status) => t(`job.enum.${status}`))
                              .join(t('dispatch.salesBridge.jobStatusSeparator')),
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">
                    {t('dispatch.salesBridge.zeroUnitHint')}
                  </p>
                </div>
              </ChecklistSection>

              <ChecklistSection
                checkKey="unpaid"
                title={t('dispatch.salesBridge.unpaidTitle')}
                count={data.unpaid.length}
                emptyText={t('dispatch.salesBridge.unpaidEmpty')}
              >
                <>
                  <ul className="divide-y rounded-md border">
                    {data.unpaid.map((order) => (
                      <li
                        key={order.order_name}
                        className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
                      >
                        <span className="font-medium">{order.order_name}</span>
                        <Badge variant="outline">
                          {t(financialStatusLabel(order.financial_status))}
                        </Badge>
                        {paidDespitePending.has(order.order_name) ? (
                          <span
                            data-testid={`sales-bridge-already-paid-${order.order_name}`}
                            className="inline-flex items-center gap-1 rounded-full border border-success/40 bg-success/10 px-2 py-0.5 text-xs font-medium text-foreground"
                          >
                            {t('dispatch.salesBridge.alreadyPaid')}
                          </span>
                        ) : null}
                        {isAdmin && order.shopify_id ? (
                          <SalesBridgeRefetchButton
                            date={date}
                            orderName={order.order_name}
                            shopifyId={order.shopify_id}
                          />
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {paidCount > 0 ? (
                    <p
                      data-testid="sales-bridge-already-paid-hint"
                      className="text-xs text-foreground"
                    >
                      {t('dispatch.salesBridge.alreadyPaidHint', { count: paidCount })}
                    </p>
                  ) : null}
                </>
              </ChecklistSection>

              <ChecklistSection
                checkKey="multiQty"
                title={t('dispatch.salesBridge.multiQtyDetailTitle')}
                count={data.multi_qty_detail.length}
                emptyText={t('dispatch.salesBridge.multiQtyDetailEmpty')}
              >
                <ul className="divide-y rounded-md border">
                  {data.multi_qty_detail.map((item) => (
                    <li
                      key={`${item.order_name}:${item.product_name}`}
                      className="px-3 py-2 text-sm font-medium"
                    >
                      {t('dispatch.salesBridge.multiQtyDetailRow', {
                        order_name: item.order_name,
                        product_name: item.product_name,
                        quantity: item.quantity,
                      })}
                    </li>
                  ))}
                </ul>
              </ChecklistSection>
            </div>

            {(data.parse_failures?.length ?? 0) > 0 ? (
              <section
                data-testid="sales-bridge-parse-failures"
                className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-3 text-destructive"
              >
                <h4 className="text-sm font-semibold">
                  {t('dispatch.salesBridge.parseFailuresTitle', {
                    count: data.parse_failures?.length ?? 0,
                  })}
                </h4>
                <ul className="space-y-1">
                  {data.parse_failures?.map((failure) => (
                    <li
                      key={failure.order_id}
                      data-testid={`sales-bridge-parse-failure-${failure.order_name}`}
                      className="text-sm"
                    >
                      <span className="font-medium">{failure.order_name}</span>
                      {' · '}
                      {t(parseFailureKindLabel(failure.error_kind))}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {allPassed ? (
              <p className="rounded-md border border-success/30 bg-success/10 px-3 py-3 text-sm font-semibold text-foreground">
                {t('dispatch.salesBridge.allPassed')}
              </p>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
